import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import LoginService from "../services/LoginService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const [token, setToken] = useState({
    access: localStorage.getItem("access_token"),
    refresh: localStorage.getItem("refresh_token"),
  });

  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const navigate = useNavigate();

  const clearLocalSession = useCallback((redirect = true) => {
    setUser(null);
    setToken({ access: null, refresh: null });
    localStorage.clear();

    if (redirect && window.location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  /* ===========================
     LOGOUT
  =========================== */
  const logout = useCallback(async () => {
    const hadRefreshToken = !!localStorage.getItem("refresh_token");
    clearLocalSession(true);

    try {
      if (hadRefreshToken) {
        await LoginService.logout();
      }
    } catch (error) {
      console.warn("Logout backend falló (ignorable)", error);
    }
  }, [clearLocalSession]);

  /* ===========================
     CARGAR PERFIL
  =========================== */
  const cargarUsuario = useCallback(async () => {
    try {
      const perfil = await LoginService.getPerfil();

      if (perfil?.data) {
        setUser(perfil.data);
        return true;
      }

      return false;
    } catch (err) {
      if (err.response?.status === 401) {
        clearLocalSession(window.location.pathname !== "/");
      }
      return false;
    }
  }, [clearLocalSession]);

  /* ===========================
     LOGIN
  =========================== */
  const applyAuthenticatedSession = async (access_token, refresh_token) => {
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    setToken({
      access: access_token,
      refresh: refresh_token,
    });

    return cargarUsuario();
  };

  const login = async (dni, password) => {
    setLoading(true);

    try {
      const res = await LoginService.login({ dni, password });

      const { access_token, refresh_token, must_change_password } = res.data;
      const ok = await applyAuthenticatedSession(access_token, refresh_token);

      setLoading(false);

      return {
        authenticated: ok,
        mustChangePassword: !!must_change_password,
      };
    } catch (error) {
      console.error("Login fallido:", error);
      setLoading(false);
      return {
        authenticated: false,
        errorMessage:
          error?.response?.data?.detail ||
          "No se pudo iniciar sesión. Verifica tus credenciales e inténtalo nuevamente.",
        statusCode: error?.response?.status || null,
      };
    }
  };

  const switchContext = async (institucionId) => {
    setLoading(true);

    try {
      const res = await LoginService.switchContext({
        institucion_id: institucionId,
      });

      const { access_token, refresh_token, must_change_password } = res.data;
      const ok = await applyAuthenticatedSession(access_token, refresh_token);

      setLoading(false);

      return {
        authenticated: ok,
        mustChangePassword: !!must_change_password,
      };
    } catch (error) {
      console.error("Cambio de contexto fallido:", error);
      setLoading(false);
      return null;
    }
  };

  /* ===========================
     RESTAURAR SESIÓN
  =========================== */
  useEffect(() => {
    const restoreSession = async () => {
      if (!token.access) {
        setInitializing(false);
        return;
      }

      await cargarUsuario();
      setInitializing(false);
    };

    restoreSession();
  }, [token.access, cargarUsuario]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        login,
        switchContext,
        logout,
        loading,
        initializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
